import { PushNotifications } from '@capacitor/push-notifications';
import { getPlatformOS, getPlatformInfo } from '../../../utils/platformUtils';
import {
  PlatformAdapter,
  PermissionStatus,
  NotificationOptions,
  NotificationChannel
} from '../types';
import { fetchApi } from '../../../lib/apiClient';
import { v4 as uuidv4 } from 'uuid';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseJSApp } from '../../../lib/firebase';

/**
 * Firebase Cloud Messaging 适配器
 * 用于处理 FCM 推送通知
 */
export class FCMAdapter implements PlatformAdapter {
  private deviceToken: string | null = null;
  private deviceId: string;
  private platformInfo = getPlatformInfo();

  constructor() {
    // 初始化设备ID
    this.deviceId = this.getOrCreateDeviceId();
    // 尝试从localStorage加载已存储的设备令牌
    this.loadStoredDeviceToken();
    // 初始化时设置监听器
    this.setupListeners();
  }

  /**
   * 获取或创建设备唯一标识符
   * @returns 设备唯一标识符
   */
  private getOrCreateDeviceId(): string {
    // 尝试从本地存储获取设备ID
    const storedDeviceId = localStorage.getItem('fcm_device_id');

    if (storedDeviceId) {
      console.log('[FCMAdapter] 使用已存储的设备ID:', storedDeviceId);
      return storedDeviceId;
    }

    // 如果不存在，创建新的设备ID
    const newDeviceId = uuidv4();
    localStorage.setItem('fcm_device_id', newDeviceId);
    console.log('[FCMAdapter] 创建新的设备ID:', newDeviceId);

    return newDeviceId;
  }

  /**
   * 从localStorage加载已存储的设备令牌
   */
  private loadStoredDeviceToken(): void {
    try {
      const storedToken = localStorage.getItem('fcm_device_token');
      if (storedToken) {
        this.deviceToken = storedToken;
        console.log('[FCMAdapter] 加载已存储的设备令牌:', storedToken.substring(0, 20) + '...');
      }
    } catch (error) {
      console.warn('[FCMAdapter] 无法加载存储的设备令牌:', error);
    }
  }

  /**
   * 获取设备唯一标识符
   * @returns 设备唯一标识符
   */
  public getDeviceId(): string {
    return this.deviceId;
  }

  /**
   * 设置 FCM 监听器
   */
  private setupListeners() {
    if (this.platformInfo.isNative) {
      // Native 平台使用 Capacitor Push Notifications
      this.setupNativeListeners();
    } else if (this.platformInfo.isWeb && !this.platformInfo.isElectron) {
      // Web 平台使用 Firebase JS SDK
      this.setupWebListeners();
    }
  }

  /**
   * 设置 Native 平台的 FCM 监听器
   */
  private setupNativeListeners() {
    // 监听设备令牌 - 在iOS上这是APNS token，在Android上这是FCM token
    PushNotifications.addListener('registration', async (token) => {
      console.log('[FCMAdapter] Native push registration success, token received:', token.value.substring(0, 20) + '...');

      // 根据平台处理不同类型的token
      if (this.platformInfo.platform === 'ios') {
        console.log('[FCMAdapter] iOS platform detected, this is APNS token, waiting for FCM token from native layer');
        // iOS平台：这是APNS token，我们需要等待FCM token
        // 不在这里处理，等待FCM token事件
      } else {
        // Android平台：这是FCM token，直接使用
        console.log('[FCMAdapter] Android platform detected, this is FCM token, processing directly');
        this.deviceToken = token.value;
        // 存储设备令牌到localStorage，供诊断信息使用
        localStorage.setItem('fcm_device_token', token.value);
        console.log('[FCMAdapter] Device token stored to localStorage for diagnostics');

        // 自动尝试发送令牌到服务器（如果用户已认证）
        try {
          await this.sendTokenToServer(token.value);
          console.log('[FCMAdapter] Device token automatically sent to server after registration');
        } catch (error) {
          console.warn('[FCMAdapter] Failed to automatically send token to server (user may not be authenticated yet):', error);
        }
      }
    });

    // 监听iOS的FCM token事件
    if (this.platformInfo.platform === 'ios') {
      this.setupIOSFCMTokenListener();
    }

    // 监听注册错误
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[FCMAdapter] Native push registration failed:', error);
    });
  }

  /**
   * 设置iOS平台的FCM token监听器
   * 监听从原生层发送的FCM token事件
   */
  private setupIOSFCMTokenListener() {
    console.log('[FCMAdapter] Setting up iOS FCM token listener');

    // 监听原生层发送的FCM token事件
    window.addEventListener('capacitorDidReceiveFCMToken', async (event: any) => {
      const fcmToken = event.detail;
      console.log('[FCMAdapter] iOS FCM token received from native layer:', fcmToken.substring(0, 20) + '...');

      this.deviceToken = fcmToken;
      // 存储设备令牌到localStorage，供诊断信息使用
      localStorage.setItem('fcm_device_token', fcmToken);
      console.log('[FCMAdapter] iOS FCM token stored to localStorage for diagnostics');

      // 自动尝试发送令牌到服务器（如果用户已认证）
      try {
        await this.sendTokenToServer(fcmToken);
        console.log('[FCMAdapter] iOS FCM token automatically sent to server after registration');
      } catch (error) {
        console.warn('[FCMAdapter] Failed to automatically send iOS FCM token to server (user may not be authenticated yet):', error);
      }
    });
  }

  /**
   * 设置 Web 平台的 FCM 监听器
   */
  private setupWebListeners() {
    if (!firebaseJSApp) {
      console.warn('[FCMAdapter] Firebase JS app not initialized, skipping web listeners setup');
      return;
    }

    try {
      const messaging = getMessaging(firebaseJSApp);

      // 监听前台消息
      onMessage(messaging, (payload) => {
        console.log('[FCMAdapter] Web foreground message received:', payload);
        // 这里可以处理前台收到的推送消息
      });
    } catch (error) {
      console.error('[FCMAdapter] Error setting up web listeners:', error);
    }
  }

  /**
   * 尝试将存储的设备令牌发送到服务器
   * 这个方法应该在用户认证成功后被调用
   */
  public async attemptSendTokenToServer(): Promise<void> {
    console.log('[FCMAdapter] attemptSendTokenToServer called.');
    if (this.deviceToken) {
      console.log(`[FCMAdapter] Found stored token ${this.deviceToken.substring(0, 20)}..., attempting to send to server.`);
      await this.sendTokenToServer(this.deviceToken);
    } else {
      console.warn('[FCMAdapter] attemptSendTokenToServer called, but no device token is stored yet.');
    }
  }

  /**
   * 自动注册设备令牌（用于用户登录后的自动注册）
   * 这个方法会检查权限、注册设备并发送令牌到服务器
   */
  public async autoRegisterDevice(): Promise<void> {
    console.log('[FCMAdapter] autoRegisterDevice called.');

    // 只在原生平台执行
    if (!this.platformInfo.isNative) {
      console.log('[FCMAdapter] Not a native platform, skipping auto registration.');
      return;
    }

    try {
      // 1. 检查权限
      const permissionStatus = await this.checkPermission();
      console.log('[FCMAdapter] Permission status:', permissionStatus);

      if (permissionStatus !== 'granted') {
        console.log('[FCMAdapter] Permission not granted, skipping auto registration.');
        return;
      }

      // 2. 如果已有令牌，直接发送到服务器
      if (this.deviceToken) {
        console.log('[FCMAdapter] Device token already exists, sending to server.');
        await this.sendTokenToServer(this.deviceToken);
        return;
      }

      // 3. 如果没有令牌，尝试注册获取新令牌
      console.log('[FCMAdapter] No device token found, attempting to register for push notifications.');
      await this.registerForPushNotifications();

      // 注册后，令牌会通过监听器异步获取，然后自动发送到服务器
      // 这里不需要等待，因为监听器会处理

    } catch (error) {
      console.error('[FCMAdapter] Error in autoRegisterDevice:', error);
    }
  }

  /**
   * 将设备令牌发送到服务器
   */
  private async sendTokenToServer(token: string) {
    try {
      // 获取平台信息 - 使用统一的平台工具
      const platformValue = getPlatformOS();

      // 确保平台值正确
      let platform: 'ios' | 'android';

      // 简化逻辑：如果是 iOS 则使用 'ios'，否则使用 'android'
      platform = platformValue === 'ios' ? 'ios' : 'android';

      // 记录平台信息
      console.log(`[FCMAdapter] 注册设备令牌，平台: ${platform}，原始平台值: ${platformValue}，设备ID: ${this.deviceId}`);

      const response = await fetchApi('/notifications/register-device', {
        method: 'POST',
        body: {
          token,
          platform,
          deviceId: this.deviceId
        }
      });

      console.log('[FCMAdapter] Token registered with server:', response);
    } catch (error) {
      console.error('[FCMAdapter] Failed to register token with server:', error);
    }
  }

  /**
   * 请求通知权限
   */
  async requestPermission(): Promise<PermissionStatus> {
    try {
      const result = await PushNotifications.requestPermissions();

      if (result.receive === 'granted') {
        // 权限获取成功，注册设备
        await this.registerForPushNotifications();
        return 'granted';
      }

      return result.receive;
    } catch (error) {
      console.error('[FCMAdapter] Permission request error:', error);
      return 'denied';
    }
  }

  /**
   * 检查通知权限
   */
  async checkPermission(): Promise<PermissionStatus> {
    try {
      const result = await PushNotifications.checkPermissions();
      return result.receive;
    } catch (error) {
      console.error('[FCMAdapter] Check permission error:', error);
      return 'denied';
    }
  }

  /**
   * 注册推送通知
   * 这个方法会向 FCM 注册设备，并获取设备令牌
   */
  async registerForPushNotifications(): Promise<string> {
    try {
      if (this.platformInfo.isNative) {
        return await this.registerNativePushNotifications();
      } else if (this.platformInfo.isWeb && !this.platformInfo.isElectron) {
        return await this.registerWebPushNotifications();
      } else {
        throw new Error('FCM push notifications not supported on this platform');
      }
    } catch (error) {
      console.error('[FCMAdapter] Register push error:', error);
      throw error;
    }
  }

  /**
   * 注册 Native 平台的推送通知
   */
  private async registerNativePushNotifications(): Promise<string> {
    // 注册设备
    await PushNotifications.register();

    // 返回当前令牌（如果有）或等待令牌
    // 注意：即使令牌已存在，调用 register() 仍可能触发 registration 事件获取新令牌
    return this.deviceToken || 'registering';
  }

  /**
   * 注册 Web 平台的推送通知
   */
  private async registerWebPushNotifications(): Promise<string> {
    if (!firebaseJSApp) {
      throw new Error('Firebase JS app not initialized');
    }

    const messaging = getMessaging(firebaseJSApp);

    // 获取 VAPID 公钥（需要在环境变量中配置）
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn('[FCMAdapter] VAPID key not configured, using default');
    }

    // 获取 FCM token
    const token = await getToken(messaging, {
      vapidKey: vapidKey
    });

    if (token) {
      console.log('[FCMAdapter] Web FCM token received:', token);
      this.deviceToken = token;
      localStorage.setItem('fcm_device_token', token);
      return token;
    } else {
      throw new Error('No registration token available');
    }
  }

  /**
   * 注销推送通知
   */
  async unregisterForPushNotifications(): Promise<void> {
    try {
      if (this.deviceToken) {
        // 从服务器删除令牌
        await fetchApi('/notifications/unregister-device', {
          method: 'DELETE',
          body: {
            token: this.deviceToken,
            deviceId: this.deviceId
          }
        });
      }

      // 移除所有监听器
      await PushNotifications.removeAllListeners();
      this.deviceToken = null;
      // 清理localStorage中的设备令牌
      localStorage.removeItem('fcm_device_token');
      console.log('[FCMAdapter] Device token removed from localStorage');
    } catch (error) {
      console.error('[FCMAdapter] Unregister push error:', error);
      throw error;
    }
  }

  /**
   * 删除当前设备令牌（用于测试）
   */
  async deleteCurrentDeviceToken(): Promise<boolean> {
    try {
      if (!this.deviceToken) {
        console.warn('[FCMAdapter] No device token to delete');
        return false;
      }

      // 从服务器删除令牌
      const response = await fetchApi('/notifications/device-token', {
        method: 'DELETE',
        body: {
          token: this.deviceToken
        }
      });

      console.log('[FCMAdapter] Token deleted from server:', response);
      this.deviceToken = null;
      // 清理localStorage中的设备令牌
      localStorage.removeItem('fcm_device_token');
      console.log('[FCMAdapter] Device token removed from localStorage');
      return true;
    } catch (error) {
      console.error('[FCMAdapter] Delete token error:', error);
      return false;
    }
  }

  /**
   * 发送本地通知
   * 注意：FCM 适配器不支持本地通知，但为了兼容接口，我们提供一个空实现
   */
  async scheduleLocalNotification(options: NotificationOptions): Promise<string> {
    console.warn('[FCMAdapter] Local notifications not supported in FCM adapter, use server-side scheduling instead');
    return options.id || 'unsupported';
  }

  /**
   * 取消本地通知
   */
  async cancelLocalNotification(_id: string): Promise<void> {
    console.warn('[FCMAdapter] Local notifications not supported in FCM adapter');
  }

  /**
   * 取消所有本地通知
   */
  async cancelAllLocalNotifications(): Promise<void> {
    console.warn('[FCMAdapter] Local notifications not supported in FCM adapter');
  }

  /**
   * 创建通知频道（仅 Android）
   */
  async createChannel?(_channel: NotificationChannel): Promise<void> {
    console.warn('[FCMAdapter] Channel creation not supported in FCM adapter');
  }

  /**
   * 打开系统设置
   */
  async openSystemSettings?(): Promise<void> {
    // console.warn('[FCMAdapter] openSettings might not be directly available or necessary');
    // PushNotifications.openSettings(); // This method might not exist or work as expected
    // throw new Error('Opening system settings not implemented for FCM adapter.');
  }
}
